import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiService } from '../../services/api';
import { Plus, Zap, AlertCircle, GitBranch } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const AdminEventMatches = () => {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [groups, setGroups] = useState([]);
  const [registeredTeams, setRegisteredTeams] = useState([]);
  const [savedBrackets, setSavedBrackets] = useState([]);
  const [bracketPreview, setBracketPreview] = useState({});
  const [generatedMatches, setGeneratedMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [allTeams, setAllTeams] = useState([]);

  const { toast } = useToast();

  useEffect(() => {
    loadEvents();
    loadAllTeams();
  }, []);

  const loadEvents = async () => {
    try {
      const response = await apiService.admin.getEvents();
      setEvents(response.data || []);
    } catch (error) {
      toast({ title: 'Gagal', description: 'Gagal memuat event', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const loadAllTeams = async () => {
    try {
      const response = await apiService.admin.getParticipants();
      setAllTeams(response.data || []);
    } catch (error) {
      console.error('Gagal memuat tim:', error);
    }
  };

  const handleSelectEvent = async (eventId) => {
    setSelectedEventId(eventId);
    const event = events.find(e => e.id === eventId);
    setSelectedEvent(event);
    setBracketPreview({});
    
    try {
      // Load groups
      if (event.format === 'tournament_elimination') {
        const groupsResponse = await apiService.admin.getEventGroups(eventId);
        setGroups(groupsResponse.data || []);
      }

      // Load registered teams
      const teamsResponse = await apiService.admin.getEventTeams(eventId);
      setRegisteredTeams(teamsResponse.data || []);

      const bracketsResponse = await apiService.admin.getEventBrackets(eventId);
      setSavedBrackets(bracketsResponse.data || []);

      setGeneratedMatches([]);
    } catch (error) {
      toast({ title: 'Gagal', description: 'Gagal memuat data event', variant: 'destructive' });
      setGroups([]);
      setRegisteredTeams([]);
      setSavedBrackets([]);
    }
  };

  const shuffleArray = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  const getStageNames = (size) => {
    const names = {
      128: '128 Besar',
      64: '64 Besar',
      32: '32 Besar',
      16: '16 Besar',
      8: 'Perempat Final',
      4: 'Semi Final',
      2: 'Final',
    };
    const stages = [];
    let current = size;
    while (current >= 2) {
      stages.push(names[current] || `${current} Besar`);
      current = current / 2;
    }
    return stages;
  };

  const buildBracketForCategory = (category, categoryRegs, size, drawMode) => {
    const teams = drawMode === 'automatic' ? shuffleArray(categoryRegs) : [...categoryRegs];
    const selected = teams.slice(0, size);
    while (selected.length < size) {
      selected.push({ team_id: null, team_name: 'BYE' });
    }

    const rounds = [];
    const stageNames = getStageNames(size);
    let current = selected;

    for (let roundIdx = 0; roundIdx < stageNames.length; roundIdx += 1) {
      const matches = [];
      const next = [];
      for (let i = 0; i < current.length; i += 2) {
        const a = current[i] || { team_name: 'TBD' };
        const b = current[i + 1] || { team_name: 'TBD' };
        matches.push({
          match_no: i / 2 + 1,
          team_a: a.team_name || getTeamName(a.team_id) || 'TBD',
          team_b: b.team_name || getTeamName(b.team_id) || 'TBD',
        });
        next.push({ team_name: `Winner M${i / 2 + 1}` });
      }
      rounds.push({ name: stageNames[roundIdx], matches });
      current = next;
    }

    return {
      category,
      size,
      drawMode,
      registered: categoryRegs.length,
      rounds,
    };
  };

  const generateBracketPreviewData = () => {
    const size = selectedEvent.bracket_size || 128;
    const drawMode = selectedEvent.draw_mode || 'automatic';
    const categories = selectedEvent.categories || [];
    const preview = {};

    categories.forEach((category) => {
      const categoryRegs = registeredTeams.filter((r) => r.category === category);
      if (categoryRegs.length >= 2) {
        preview[category] = buildBracketForCategory(category, categoryRegs, size, drawMode);
      }
    });

    return preview;
  };

  const handleGenerateBracketPreview = () => {
    if (!selectedEvent || selectedEvent.format !== 'tournament_elimination') {
      toast({ title: 'Info', description: 'Fitur drawing hanya untuk format eliminasi turnamen' });
      return;
    }

    const preview = generateBracketPreviewData();

    setBracketPreview(preview);
    if (Object.keys(preview).length === 0) {
      toast({ title: 'Info', description: 'Minimal 2 tim per kategori untuk membuat bagan drawing' });
    } else {
      toast({ title: 'Berhasil', description: 'Pratinjau bagan drawing berhasil dibuat' });
    }
  };

  const handleSaveBracket = async (category) => {
    const bracket = bracketPreview[category];
    if (!bracket) return;

    try {
      await apiService.admin.saveEventBracket(selectedEventId, {
        event_id: selectedEventId,
        category,
        bracket_size: bracket.size,
        draw_mode: bracket.drawMode,
        teams: registeredTeams
          .filter((r) => r.category === category)
          .map((r) => ({ team_id: r.team_id, team_name: r.team_name })),
        rounds: bracket.rounds,
        registered_team_count: registeredTeams.filter((r) => r.category === category).length,
      });

      const bracketsResponse = await apiService.admin.getEventBrackets(selectedEventId);
      setSavedBrackets(bracketsResponse.data || []);
      toast({ title: 'Berhasil', description: `Bagan drawing ${category} berhasil disimpan` });
    } catch (error) {
      toast({
        title: 'Gagal',
        description: error.response?.data?.detail || 'Gagal menyimpan bagan',
        variant: 'destructive',
      });
    }
  };

  const generateRoundRobinMatches = (teams, groupId) => {
    const matches = [];
    const teamArray = [...teams];
    
    // Round-robin algorithm
    for (let i = 0; i < teamArray.length; i++) {
      for (let j = i + 1; j < teamArray.length; j++) {
        matches.push({
          team_a_id: teamArray[i],
          team_b_id: teamArray[j],
          group_id: groupId,
          status: 'scheduled'
        });
      }
    }
    
    return matches;
  };

  const handleGenerateMatches = async () => {
    if (!selectedEvent) {
      toast({ title: 'Gagal', description: 'Pilih event terlebih dahulu', variant: 'destructive' });
      return;
    }

    setGenerating(true);
    try {
      const matches = [];
      let preview = {};

      if (selectedEvent.format === 'tournament_elimination') {
        preview = generateBracketPreviewData();
        setBracketPreview(preview);

        // Generate Round 1 matches from bracket preview
        Object.keys(preview).forEach((category) => {
          const bracket = preview[category];
          if (bracket.rounds.length > 0) {
            const round1 = bracket.rounds[0];
            round1.matches.forEach((match) => {
              if (match.team_a !== 'BYE' && match.team_b !== 'BYE' &&
                  match.team_a !== 'TBD' && match.team_b !== 'TBD') {
                matches.push({
                  team_a_name: match.team_a,
                  team_b_name: match.team_b,
                  category,
                  round_name: round1.name,
                  match_no: match.match_no,
                  status: 'scheduled',
                });
              }
            });
          }
        });
      } else {
        // League format - generate by category
        const categories = selectedEvent.categories || [];
        
        for (const category of categories) {
          const categoryTeams = registeredTeams
            .filter(reg => reg.category === category)
            .map(reg => reg.team_id);

          if (categoryTeams.length > 1) {
            const categoryMatches = generateRoundRobinMatches(categoryTeams, null);
            
            // Add league_type for league format
            categoryMatches.forEach(match => {
              match.category = category;
              match.type = selectedEvent.league_type || 'one_match';
            });

            matches.push(...categoryMatches);
          }
        }
      }

      if (matches.length === 0 && Object.keys(preview).length === 0) {
        toast({ 
          title: 'Info', 
          description: 'Tidak ada pertandingan yang dapat dibuat. Pastikan minimal ada 2 tim di setiap grup/kategori.',
          variant: 'default'
        });
        setGenerating(false);
        return;
      }

      setGeneratedMatches(matches);
      if (selectedEvent.format === 'tournament_elimination') {
        const bracketCount = Object.keys(preview).length;
        if (matches.length > 0) {
          toast({ title: 'Berhasil', description: `${matches.length} pertandingan berhasil dibuat • drawing ${bracketCount} kategori siap disimpan` });
        } else {
          toast({ title: 'Berhasil', description: `Drawing ${bracketCount} kategori siap disimpan` });
        }
      } else {
        toast({ title: 'Berhasil', description: `${matches.length} pertandingan berhasil dibuat` });
      }
    } catch (error) {
      toast({ title: 'Gagal', description: 'Gagal membuat pertandingan', variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveMatches = async () => {
    if (generatedMatches.length === 0) {
      toast({ title: 'Gagal', description: 'Tidak ada pertandingan untuk disimpan', variant: 'destructive' });
      return;
    }

    try {
      // Save each match
      for (const match of generatedMatches) {
        const homeTeamName = match.team_a_name || getTeamName(match.team_a_id);
        const awayTeamName = match.team_b_name || getTeamName(match.team_b_id);

        await apiService.admin.createMatch({
          event_id: selectedEventId,
          home_team: homeTeamName,
          away_team: awayTeamName,
          match_format: match.round_name ? 'elimination' : 'round_robin',
          home_score: null,
          away_score: null,
          date: new Date().toISOString(),
          time: '16:00',
          venue: 'Menyusul',
          status: match.status || 'scheduled',
          category: match.category || null,
          group_id: match.group_id || null,
          round_name: match.round_name || null,
          goal_events: [],
        });
      }

      toast({ title: 'Berhasil', description: `${generatedMatches.length} pertandingan berhasil disimpan` });
      setGeneratedMatches([]);
      
      // Refresh event
      if (selectedEventId) {
        handleSelectEvent(selectedEventId);
      }
    } catch (error) {
      toast({ 
        title: 'Gagal', 
        description: error.response?.data?.detail || 'Gagal menyimpan pertandingan',
        variant: 'destructive' 
      });
    }
  };

  const getTeamName = (teamId) => {
    return allTeams.find(t => t.id === teamId)?.name || teamId;
  };

  const getGroupInfo = (groupId) => {
    const group = groups.find(g => g.id === groupId);
    return group?.group_name || groupId;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'U-10': 'bg-blue-100 text-blue-800',
      'U-12': 'bg-green-100 text-green-800',
      'U-14': 'bg-purple-100 text-purple-800',
      'U-17': 'bg-orange-100 text-orange-800',
      'U-21': 'bg-red-100 text-red-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getGenerateButtonLabel = () => {
    if (!selectedEvent) return 'Susun';
    if (generating) return 'Memproses...';

    if (selectedEvent.format === 'tournament_elimination') {
      return generatedMatches.length === 0
        ? 'Susun Pertandingan & Drawing'
        : `Susun Ulang Pertandingan & Drawing (${generatedMatches.length} tersusun)`;
    }

    return generatedMatches.length === 0
      ? 'Susun Pertandingan'
      : `Susun Ulang Pertandingan (${generatedMatches.length} tersusun)`;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="animate-pulse">Memuat...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 p-4 sm:p-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Drawing & Generator Pertandingan</h1>
          <p className="text-gray-600 mt-2 text-sm sm:text-base">Buat bracket drawing dan susun pertandingan dari event terpilih</p>
        </div>

        {/* Event Selection */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <h2 className="text-lg font-semibold mb-4">Pilih Event</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {events.map(event => (
              <button
                key={event.id}
                onClick={() => handleSelectEvent(event.id)}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  selectedEventId === event.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <h3 className="font-semibold text-gray-900">{event.name}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Format: {event.format === 'league_round_robin' ? 'Liga' : 'Turnamen'}
                </p>
                {event.format === 'tournament_elimination' && (
                  <p className="text-sm text-gray-600">Jumlah Grup: {event.group_count}</p>
                )}
                <p className="text-xs text-gray-500 mt-2">Status: {event.status}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Event Details */}
        {selectedEvent && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">{selectedEvent.name}</h2>

            {/* Event Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-gray-600">Format</p>
                <p className="font-semibold">
                  {selectedEvent.format === 'league_round_robin' 
                    ? `Liga (${selectedEvent.league_type === 'home_away' ? 'Kandang & Tandang' : 'Sekali Tanding'})`
                    : 'Eliminasi Turnamen'
                  }
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Tim Terdaftar</p>
                <p className="font-semibold">{registeredTeams.length} tim</p>
              </div>
            </div>

            {selectedEvent.format === 'tournament_elimination' && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="text-sm text-indigo-900">
                      <span className="font-semibold">Informasi Drawing:</span> Bagan {selectedEvent.bracket_size || 128} • mode {selectedEvent.draw_mode === 'manual' ? 'manual' : 'otomatis'}
                    </p>
                    <p className="text-sm text-indigo-900 mt-1">
                      Bagan tersimpan: {savedBrackets.length} kategori • Gunakan tombol Susun Pertandingan lalu Simpan Bagan.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {selectedEvent.format === 'tournament_elimination' && Object.keys(bracketPreview).length > 0 && (
              <div className="mb-6 space-y-4">
                {Object.values(bracketPreview).map((preview) => (
                  <div key={preview.category} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center gap-2 mb-3">
                      <GitBranch className="h-4 w-4 text-gray-700" />
                      <h3 className="font-semibold text-gray-900">
                        {preview.category} - Bagan {preview.size} ({preview.drawMode === 'manual' ? 'Manual' : 'Otomatis'})
                      </h3>
                      <button
                        onClick={() => handleSaveBracket(preview.category)}
                        className="ml-auto px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                      >
                        Simpan Bagan
                      </button>
                    </div>
                    <div className="overflow-x-auto">
                      <div className="flex gap-4 min-w-max pb-2">
                        {preview.rounds.map((round) => (
                          <div key={round.name} className="w-56">
                            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">{round.name}</p>
                            <div className="space-y-2">
                              {round.matches.map((m) => (
                                <div key={`${round.name}-${m.match_no}`} className="bg-white border rounded p-2 text-xs">
                                  <p className="font-medium text-gray-800">{m.team_a}</p>
                                  <p className="text-gray-400">vs</p>
                                  <p className="font-medium text-gray-800">{m.team_b}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Groups Info (Tournament) */}
            {selectedEvent.format === 'tournament_elimination' && groups.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-900 mb-2">Grup Turnamen</h3>
                <div className="space-y-2">
                  {groups.map(group => (
                    <div key={group.id} className="text-sm text-blue-800">
                      <span className="font-medium">{group.group_name}:</span> {group.teams?.length || 0} tim
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Categories Info (League) */}
            {selectedEvent.format === 'league_round_robin' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-green-900 mb-2">Kategori Liga</h3>
                <div className="space-y-2">
                  {selectedEvent.categories?.map(category => {
                    const categoryCount = registeredTeams.filter(r => r.category === category).length;
                    return (
                      <div key={category} className="text-sm text-green-800">
                        <span className="font-medium">{category}:</span> {categoryCount} tim
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Generate Matches Button */}
            <button
              onClick={handleGenerateMatches}
              disabled={generating || registeredTeams.length < 2}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold text-sm sm:text-base"
            >
              <Zap size={20} />
              {getGenerateButtonLabel()}
            </button>

            {selectedEvent.format === 'tournament_elimination' && registeredTeams.length >= 2 && (
              <p className="mt-3 text-sm text-gray-600">
                Tombol ini sekaligus menyiapkan pratinjau drawing. Setelah itu klik <span className="font-semibold">Simpan Bagan</span> pada kategori yang diinginkan.
              </p>
            )}

            {registeredTeams.length < 2 && (
              <div className="mt-4 flex items-center gap-2 text-orange-700 bg-orange-50 border border-orange-200 rounded-lg p-3">
                <AlertCircle size={18} />
                <span className="text-sm">
                  Minimal 2 tim diperlukan untuk {selectedEvent.format === 'tournament_elimination' ? 'menyusun pertandingan & drawing' : 'menyusun pertandingan'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Generated Matches Preview */}
        {generatedMatches.length > 0 && (
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <h2 className="text-lg font-semibold">Pertandingan Tersusun ({generatedMatches.length})</h2>
              <button
                onClick={handleSaveMatches}
                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 font-medium"
              >
                <Plus size={20} />
                Simpan Semua Pertandingan
              </button>
            </div>

            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-3 font-semibold text-gray-900">Tim A</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-900">vs</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-900">Tim B</th>
                    {selectedEvent?.format === 'tournament_elimination' && (
                      <th className="text-left px-4 py-3 font-semibold text-gray-900">Babak</th>
                    )}
                    {selectedEvent?.format === 'league_round_robin' && (
                      <th className="text-left px-4 py-3 font-semibold text-gray-900">Kategori</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {generatedMatches.map((match, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900">
                          {match.team_a_name || getTeamName(match.team_a_id)}
                        </span>
                      </td>
                      <td className="text-center px-4 py-3 text-gray-500">vs</td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900">
                          {match.team_b_name || getTeamName(match.team_b_id)}
                        </span>
                      </td>
                      {selectedEvent?.format === 'tournament_elimination' && (
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600">
                            {match.round_name || '-'}
                          </span>
                        </td>
                      )}
                      {selectedEvent?.format === 'league_round_robin' && (
                        <td className="px-4 py-3">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(match.category)}`}>
                            {match.category}
                          </span>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminEventMatches;
